import React, { useMemo } from 'react'
import styled from 'styled-components'

const MONO_FONT = `ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace`

const DiffContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.375rem;
  overflow: hidden;
  max-height: 18rem;
  overflow-y: auto;
`

const DiffPre = styled.pre`
  font-family: ${MONO_FONT};
  font-size: 0.7rem;
  line-height: 1.5;
  margin: 0;
  padding: 0.5rem 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.forecast.defaultText};
  background: ${({ theme }) =>
    theme.forecast.pageBackground1 ?? theme.forecast.background1};
`

const DiffLineAdded = styled.span`
  color: #10b981;
`

const DiffLineRemoved = styled.span`
  color: #ef4444;
  text-decoration: line-through;
`

export interface LineDiffProps {
  oldText: string
  newText: string
}

/**
 * Plain-text line-by-line diff. Lines that match are shown unchanged;
 * mismatches render the old line struck-through above the new line in
 * green. Designed for short-to-medium snippets (notes, drafts) — not
 * a true LCS diff, just a positional comparison, which is sufficient
 * for proposal cards where the model produces wholesale replacements.
 */
export const LineDiff: React.FC<LineDiffProps> = ({ oldText, newText }) => {
  const lines = useMemo(() => {
    const oldLines = (oldText || '').split('\n')
    const newLines = (newText || '').split('\n')
    const maxLen = Math.max(oldLines.length, newLines.length)
    const result: Array<{ type: 'same' | 'add' | 'remove'; text: string }> = []

    for (let i = 0; i < maxLen; i++) {
      const oldLine = i < oldLines.length ? oldLines[i] : undefined
      const newLine = i < newLines.length ? newLines[i] : undefined

      if (oldLine === newLine) {
        result.push({ type: 'same', text: oldLine ?? '' })
      } else {
        if (oldLine !== undefined)
          result.push({ type: 'remove', text: oldLine })
        if (newLine !== undefined) result.push({ type: 'add', text: newLine })
      }
    }
    return result
  }, [oldText, newText])

  return (
    <DiffContainer>
      <DiffPre>
        {lines.map((line, i) => {
          if (line.type === 'add') {
            return (
              <React.Fragment key={i}>
                <DiffLineAdded>{`+ ${line.text}`}</DiffLineAdded>
                {'\n'}
              </React.Fragment>
            )
          }
          if (line.type === 'remove') {
            return (
              <React.Fragment key={i}>
                <DiffLineRemoved>{`- ${line.text}`}</DiffLineRemoved>
                {'\n'}
              </React.Fragment>
            )
          }
          return (
            <React.Fragment key={i}>
              {`  ${line.text}`}
              {'\n'}
            </React.Fragment>
          )
        })}
      </DiffPre>
    </DiffContainer>
  )
}
